import { AccountingEntityType } from '@numeo/types-main-web-app'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { batchToggleAccountingReadStatus, getAccountingReadStatus, toggleAccountingReadStatus } from 'services/invoiceApi'

interface AccountingReadStatusOptions {
    /**
     * While the read marks of the ids on screen are still being fetched (a new tab, a new page), report them as read instead of unread. A set that
     * has not arrived yet holds none of them, which read as every row unread until it did.
     */
    holdUnknown?: boolean
}

export const useAccountingReadStatus = (entityIds: string[], entityType: AccountingEntityType, { holdUnknown = false }: AccountingReadStatusOptions = {}) => {
    const [readIds, setReadIds] = useState<Set<string>>(new Set())
    // The ids `readIds` was fetched for.
    const [loadedKey, setLoadedKey] = useState('')
    const wantedKey = useMemo(() => [...entityIds].sort().join(','), [entityIds])
    const unknown = holdUnknown && entityIds.length > 0 && loadedKey !== wantedKey
    const [loading, setLoading] = useState(false)
    const prevIdsRef = useRef<string>('')

    // The ids the effect asks about, by value: an array that is new on every render must not cancel the request in flight and, matching the key it
    // already asked for, never ask again (which left every row read).
    const idsRef = useRef(entityIds)
    idsRef.current = entityIds
    useEffect(() => {
        const key = wantedKey
        if (key === prevIdsRef.current || key === '') return
        prevIdsRef.current = key

        let cancelled = false
        setLoading(true)
        getAccountingReadStatus(idsRef.current, entityType).then((res) => {
            if (cancelled) return
            if (res.success && res.data) {
                setReadIds(new Set(res.data.readEntityIds))
            }
            // Also when the fetch failed: unknown must not last for ever.
            setLoadedKey(key)
            setLoading(false)
        })

        return () => {
            cancelled = true
            // A request that is cancelled before it answered is asked again, whatever the ids were.
            if (prevIdsRef.current === key) prevIdsRef.current = ''
        }
    }, [wantedKey, entityType])

    const unreadCount = useMemo(() => {
        if (entityIds.length === 0 || unknown) return 0
        return entityIds.filter((id) => !readIds.has(id)).length
    }, [entityIds, readIds, unknown])

    const isRead = useCallback((id: string) => unknown || readIds.has(id), [readIds, unknown])

    const toggleRead = useCallback(
        async (id: string) => {
            const wasRead = readIds.has(id)
            setReadIds((prev) => {
                const next = new Set(prev)
                if (wasRead) next.delete(id)
                else next.add(id)
                return next
            })

            const res = await toggleAccountingReadStatus(id, entityType, !wasRead)
            if (!res.success) {
                setReadIds((prev) => {
                    const next = new Set(prev)
                    if (wasRead) next.add(id)
                    else next.delete(id)
                    return next
                })
            }
        },
        [readIds, entityType]
    )

    const markRead = useCallback(
        async (id: string) => {
            if (readIds.has(id)) return
            setReadIds((prev) => new Set(prev).add(id))
            const res = await toggleAccountingReadStatus(id, entityType, true)
            if (!res.success) {
                setReadIds((prev) => {
                    const next = new Set(prev)
                    next.delete(id)
                    return next
                })
            }
        },
        [readIds, entityType]
    )

    const bulkToggleRead = useCallback(
        async (ids: string[], markAsRead: boolean) => {
            if (ids.length === 0) return
            // Optimistic update
            setReadIds((prev) => {
                const next = new Set(prev)
                for (const id of ids) {
                    if (markAsRead) next.add(id)
                    else next.delete(id)
                }
                return next
            })

            const res = await batchToggleAccountingReadStatus(ids, entityType, markAsRead)

            if (!res.success) {
                setReadIds((prev) => {
                    const next = new Set(prev)
                    for (const id of ids) {
                        if (markAsRead) next.delete(id)
                        else next.add(id)
                    }
                    return next
                })
            }
        },
        [entityType]
    )

    return { readIds, isRead, toggleRead, markRead, bulkToggleRead, unreadCount, loading }
}
